/** The caller's next appointments for the calendar widget (#1823). */

import { tqsProxyFetch } from '@/components/applications/communicator2/lib/proxy'

export interface UpcomingEvent {
  subject: string
  /** Local wall-clock time of the mailbox, YYYY-MM-DDTHH:MM. */
  start: string | null
  end: string | null
  all_day: boolean
  location: string
  online_meeting: boolean
}

export type CalendarState = 'connected' | 'not_connected' | 'expired' | 'unavailable'

export interface UpcomingEvents {
  state: CalendarState
  events: UpcomingEvent[]
}

export const localDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export async function fetchUpcomingEvents(now: Date): Promise<UpcomingEvents> {
  const response = await tqsProxyFetch(`/connectors/microsoft365/calendar/upcoming?day=${localDay(now)}`)
  return (await response.json()) as UpcomingEvents
}

export interface AgendaItem {
  event: UpcomingEvent
  running: boolean
  minutesLeft: number
}

/** Today's appointments still to come, and the first one of tomorrow. */
export function buildAgenda(events: UpcomingEvent[], now: Date): { today: AgendaItem[]; tomorrow: UpcomingEvent | null } {
  const today = localDay(now)
  const inRange = events.filter((event) => event.start)
  const items = inRange
    .filter((event) => event.start!.startsWith(today) && !event.all_day)
    .map((event) => {
      const start = new Date(event.start!)
      const end = new Date(event.end ?? event.start!)
      return {
        event,
        running: start <= now && now < end,
        minutesLeft: Math.ceil((end.getTime() - now.getTime()) / 60000),
        ended: end <= now,
      }
    })
    .filter((item) => !item.ended)
    .map(({ event, running, minutesLeft }) => ({ event, running, minutesLeft }))
  const allDay = inRange
    .filter((event) => event.start!.startsWith(today) && event.all_day)
    .map((event) => ({ event, running: false, minutesLeft: 0 }))
  const tomorrow = inRange.find((event) => !event.start!.startsWith(today) && !event.all_day) ?? null
  return { today: [...items, ...allDay], tomorrow }
}

export interface MonthCell {
  day: number | null
  today: boolean
}

/** The current month as a Monday-first grid, with leading blanks. */
export function monthGrid(now: Date): MonthCell[] {
  const year = now.getFullYear()
  const month = now.getMonth()
  const blanks = (new Date(year, month, 1).getDay() + 6) % 7
  const days = new Date(year, month + 1, 0).getDate()
  return [
    ...Array.from({ length: blanks }, () => ({ day: null, today: false })),
    ...Array.from({ length: days }, (_, index) => ({ day: index + 1, today: index + 1 === now.getDate() })),
  ]
}
